<template>
  <!-- To be integrated: bg-[#f6f4f0]  -->
  <div class="flex flex-col h-full">
    <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
    <div v-if="loading" class="pt-20 mx-auto">Loading...</div>
    <div v-else-if="personalId" class="flex flex-col pt-10 px-8">
      <div class="font-black text-[2.25rem] leading-[2.375rem] text-[#242425] tracking-[-0.03em]">
        My QR Code
      </div>
      <div class="bg-[#FFFFFF] rounded-[20px] mt-20">
        <section class="bg-[#ffffff] rounded-[20px] flex p-10 items-center justify-center">
          <!-- Short-lived token (expires after 60s) when Scan to Print is on; the
               static personal ID otherwise, or if the server says it is off. -->
          <OneTimeQrCode v-if="useOneTimeQr" @unavailable="tokenUnavailable = true" />
          <qrcode-vue v-else :value="personalId" :size="230" level="H" />
        </section>
      </div>
      <p class="mx-auto text-[1rem] leading-[1.5rem] mb-[12px] mt-[16px] font-semibold">
        Personal ID: {{ personalId }}
      </p>
    </div>
    <div v-else class="pt-[85px] px-[32px]">
      <div class="text-[2.25rem] leading-[2.375rem] not-italic font-black text-[#242425]">
        Seems like you don't have a QR code yet
      </div>
      <div class="pt-4 text-[1rem] leading-[1.5rem] not-italic font-normal text-[#2C2E34]">
        Generate your QR code today
      </div>
      <div class="flex flex-col gap-1 mt-6">
        <CustomTextField
          label="Date of Birth"
          type="date"
          error-msg="Enter a valid date"
          v-model="payload.dateOfBirth"
        />
      </div>
      <div class="flex flex-col gap-1 mt-4">
        <CustomTextField
          label="Last 4 chars of NRIC/FIN/Passport no."
          placeholder="Eg: 123A"
          error-msg="Must contain 3 numbers and 1 letter e.g. 123A"
          :disabled="fieldDisable"
          :textLimit="4"
          v-model="payload.partialNric"
        />
      </div>
      <BottomButton
        text="Generate QR Code"
        class="w-full leading-6 flex items-center justify-center mt-6 rounded-[36px] h-[56px]"
        solid-classes="bg-[#3D3A35] text-white text-[1rem] leading-[1.25rem] font-semibold"
        :disabled="!payload.partialNric && !payload.dateOfBirth"
        @click="updateQRCode"
      />
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import CustomTextField from '@/components/design/CustomTextField.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import QrcodeVue from 'qrcode.vue';
import BackButton from '@/components/common/BackButton.vue';
import OneTimeQrCode from '@/components/HomePage/OneTimeQrCode.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { SCAN_PRINT_FLAG } from '@/constants/scan-print';

export default {
  components: {
    CustomTextField,
    BottomButton,
    QrcodeVue,
    BackButton,
    OneTimeQrCode,
  },
  data() {
    return {
      // The server said the flag is off (404) though the app thought it on.
      tokenUnavailable: false,
      payload: {
        familyName: '',
        givenName: '',
        preferredName: null,
        gender: '',
        dateOfBirth: '',
        partialNric: '',
        personalId: '',
        hasBioUpdateRequest: false,
        mobileNumber: '',
        email: '',
        stageOfLife: '',
        maritalStatus: '',
        hasChildren: true,
        cg: '',
        countryCode: '',
        schoolId: null,
      },
    };
  },
  computed: {
    ...mapState('profile', ['dob', 'uin', 'personalId', 'loading']),
    // Resolved inside the computed, as courses/index.vue does, so a spec's
    // hoisted jest.mock of the composable is in place when it runs.
    scanPrintEnabled() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(SCAN_PRINT_FLAG);
    },
    useOneTimeQr() {
      return this.scanPrintEnabled && !this.tokenUnavailable;
    },
    ...mapGetters({
      originalPayload: 'profile/payload',
    }),
    errorMsg() {
      let details = '';
      if (!this.dob && !this.uin) {
        details = 'last 4 characters of your NRIC/FIN and Date of Birth';
      } else if (!this.dob) {
        details = 'Date of Birth';
      } else if (!this.uin) {
        details = 'last 4 characters of your NRIC/FIN';
      } else if (!this.personalId) {
        return 'There a hiccup in generating your Personal ID. Fret not, our friendly IT team will be in touch with you over email / SMS.';
      }
      return `Update your ${details} under ‘Edit Profile’ to view your unique QR code here!`;
    },
  },
  async mounted() {
    // Fetch profile data first to ensure we have the latest payload
    await this.$store.dispatch('profile/fetchProfile');
  },
  watch: {
    originalPayload: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length) {
          this.payload = {
            ...this.payload,
            ...newVal,
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    back() {
      this.$router.push('/home');
    },
    async updateQRCode() {
      const profileData = {
        dateOfBirth: this.payload.dateOfBirth,
        partialNric: this.payload.partialNric,
      };

      await this.$store.dispatch('profile/updateProfile', profileData);
      await this.$store.dispatch('profile/forceFetchProfile');

      this.$router.push('/my-qr');
    },
  },
};
</script>
